import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { buildRouteKpiValues } from '../routeKpiValues'

export const ROUTE_EXPORT_SEGMENTS = {
  general: { label: 'General', activity: 'actividad', title: 'visita' },
  order: { label: 'Pedido', activity: 'pedido', title: 'pedidos' },
  delivery: { label: 'Entrega', activity: 'entrega', title: 'entregas' },
  charge: { label: 'Cobro', activity: 'cobro', title: 'cobros' }
}

export const buildRouteExportKpis = (routeCartData, segment = 'general') => {
  const values = buildRouteKpiValues(routeCartData)
  const selected = ROUTE_EXPORT_SEGMENTS[segment] || ROUTE_EXPORT_SEGMENTS.general
  const geofence = values.geofence?.[segment]
  const display = (value) => value ?? '—'
  const percent = (value) => (value == null ? '—' : `${value}%`)
  return [
    {
      title: 'Efectividad de visita',
      value: percent(values.effectiveness),
      subtitle: 'Clientes visitados físicamente en ruta / Meta de clientes en el período',
      metrics: [
        ['Clientes en ruta (Meta)', display(values.planned)],
        ['Clientes visitados en ruta', display(values.visited)],
        ['Clientes sin visitar', display(values.unvisited)],
        ['Visitados fuera de ruta', display(values.outside)]
      ],
      footer: `Total visitados: ${display(values.totalVisited)}`
    },
    {
      title: `Geocerca · ${selected.label}`,
      value: percent(geofence?.conversion),
      subtitle: `Conversión de ${selected.title}: con ${selected.activity} en geocerca / Clientes únicos visitados`,
      metrics: [
        ['Clientes únicos visitados', display(geofence?.visited_clients)],
        [`Clientes únicos con ${selected.activity} en geocerca`, display(geofence?.active_clients)]
      ],
      footer:
        geofence?.conversion == null
          ? geofence
            ? 'Sin clientes únicos visitados para calcular la conversión.'
            : 'Datos de geocerca no disponibles.'
          : ''
    }
  ]
}

export const routeBusinessLines = (business = {}) => {
  const first = (...values) =>
    values.find((value) => value != null && String(value).trim()) || 'N/A'
  return [
    first(
      business.business_description,
      business.description,
      business.business_name,
      business.commercial_name,
      business.legal_name,
      business.name
    ),
    `RUC: ${first(business.ruc, business.nit, business.tax_id, business.taxId, business.business_ruc)}`,
    `Dirección: ${first(business.address, business.business_address, business.location, business.direction)}`,
    `Teléfono: ${first(business.phone_number, business.phone, business.telephone, business.mobile, business.business_phone)}`
  ]
}

// Letter landscape, like refund approval. All dimensions are in points.
export const buildUserReportPdf = ({
  business,
  salesmanName,
  salesmanCode,
  period,
  generatedAt,
  kpis,
  rows,
  title = 'Ejecución de plan de visitas',
  tableTitle = 'VISITAS · RESUMEN',
  headers = [
    'Cliente',
    'Fecha planificada',
    'Fecha de ejecución',
    'Duración de visita/atención',
    'Dentro de ruta',
    'Efectividad',
    'Actividades realizadas',
    'Estado'
  ],
  columnStyles = {
    0: { cellWidth: 155 },
    1: { cellWidth: 69, halign: 'center' },
    2: { cellWidth: 69, halign: 'center' },
    3: { cellWidth: 78, halign: 'center' },
    4: { cellWidth: 58 },
    5: { cellWidth: 58 },
    6: { cellWidth: 155 },
    7: { cellWidth: 94 }
  },
  chipColumns = [4, 5, 7],
  totals = [],
  onDrawCell
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })
  const margin = 28
  const width = doc.internal.pageSize.getWidth() - margin * 2
  const right = margin + width
  const ink = [32, 37, 45]
  const muted = [112, 124, 141]
  const border = [213, 220, 228]
  const text = (value, x, y, size = 9, bold = false, color = ink, options = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(size)
    doc.setTextColor(...color)
    doc.text(String(value ?? '—'), x, y, options)
  }
  let y = 36
  for (const line of [
    ...routeBusinessLines(business),
    `Usuario: ${[salesmanCode, salesmanName].filter(Boolean).join(' - ') || 'N/A'}`
  ]) {
    doc.setFontSize(9)
    const lines = doc.splitTextToSize(line, width * 0.52)
    text(lines.join('\n'), margin, y)
    y += lines.length * 12
  }
  text(title, right, 40, 13, true, ink, { align: 'right' })
  text(`Período: ${period}`, right, 59, 9, false, ink, { align: 'right' })
  text(`Generado: ${generatedAt}`, right, 74, 9, false, ink, { align: 'right' })
  y = Math.max(y + 8, 110)
  doc.setDrawColor(...border)
  doc.line(margin, y, right, y)
  y += 18
  const cardWidth = (width - 12) / 2
  const cardHeight = 202
  kpis.forEach((card, index) => {
    const x = margin + index * (cardWidth + 12)
    const accent = index === 0 ? [185, 103, 40] : [68, 108, 152]
    doc.setFillColor(250, 251, 252)
    doc.setDrawColor(...border)
    doc.roundedRect(x, y, cardWidth, cardHeight, 7, 7, 'FD')
    text(card.title, x + 14, y + 23, 11, true)
    text(card.value, x + 14, y + 59, 27, true, accent)
    doc.setFontSize(8)
    text(
      doc.splitTextToSize(card.subtitle, cardWidth - 28).join('\n'),
      x + 14,
      y + 77,
      8,
      false,
      muted
    )
    card.metrics.forEach(([label, value], metricIndex) => {
      const mx = x + 14 + (metricIndex % 2) * (cardWidth / 2)
      const my = y + 116 + Math.floor(metricIndex / 2) * 36
      text(value, mx, my, 15, true, metricIndex === 3 ? [180, 66, 58] : ink)
      doc.setFontSize(7.5)
      text(
        doc.splitTextToSize(label, cardWidth / 2 - 24).join('\n'),
        mx,
        my + 12,
        7.5,
        false,
        muted
      )
    })
    text(card.footer, x + 14, y + cardHeight - 12, 7.5, false, muted)
  })
  if (kpis.length) y += cardHeight + 25
  else y += 5
  if (totals.length) {
    const totalWidth = (width - (totals.length - 1) * 10) / totals.length
    totals.forEach((total, index) => {
      const x = margin + index * (totalWidth + 10)
      doc.setFillColor(250, 251, 252)
      doc.setDrawColor(...border)
      doc.roundedRect(x, y - 5, totalWidth, 72, 5, 5, 'FD')
      text(total.title, x + 10, y + 10, 9, true)
      doc.setFontSize(16)
      const size = Math.min(
        16,
        (16 * (totalWidth - 20)) / Math.max(doc.getTextWidth(String(total.value)), 1)
      )
      text(total.value, x + 10, y + 33, size, true)
      text(total.footer, x + 10, y + 51, 8, false, muted)
    })
    y += 90
  }
  text(tableTitle, margin, y, 10, true)
  const palette = (value) => {
    const label = String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
    if (['SI', 'VISITADO'].includes(label))
      return [
        [218, 239, 229],
        [24, 112, 76]
      ]
    if (['NO', 'NO VISITADO'].includes(label))
      return [
        [252, 229, 226],
        [171, 57, 49]
      ]
    if (label === 'PENDIENTE')
      return [
        [255, 242, 205],
        [140, 99, 18]
      ]
    return [
      [232, 237, 243],
      [76, 95, 119]
    ]
  }
  autoTable(doc, {
    startY: y + 12,
    margin: { top: 34, left: margin, right: margin, bottom: 46 },
    head: [headers],
    body: rows.length ? rows : [[{ content: 'No hay resultados', colSpan: headers.length }]],
    theme: 'grid',
    rowPageBreak: 'avoid',
    styles: {
      fontSize: 8,
      cellPadding: 6,
      textColor: ink,
      lineColor: border,
      lineWidth: 0.5,
      minCellHeight: 28,
      valign: 'middle',
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [158, 154, 154],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    },
    alternateRowStyles: { fillColor: [250, 251, 252] },
    columnStyles,
    didParseCell: ({ cell, column, section }) => {
      if (section === 'body' && chipColumns.includes(column.index)) {
        cell.styles.halign = 'center'
        cell.styles.textColor = cell.styles.fillColor || [255, 255, 255]
      }
    },
    didDrawCell: (data) => {
      onDrawCell?.(data, doc)
      const { cell, column, section } = data
      if (section !== 'body' || !chipColumns.includes(column.index)) return
      const label = String(cell.raw || '-')
      const [bg, color] = /ANULAD|RECHAZAD/i.test(label) ? palette('NO') : palette(label)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(7)
      const lines = doc.splitTextToSize(label, cell.width - 20)
      const pillHeight = Math.max(17, lines.length * 8 + 8)
      const pillWidth = Math.min(
        cell.width - 8,
        Math.max(...lines.map((line) => doc.getTextWidth(line))) + 14
      )
      const px = cell.x + (cell.width - pillWidth) / 2
      const py = cell.y + (cell.height - pillHeight) / 2
      doc.setFillColor(...bg)
      doc.roundedRect(px, py, pillWidth, pillHeight, 8, 8, 'F')
      text(lines.join('\n'), cell.x + cell.width / 2, py + 11, 7, true, color, { align: 'center' })
    }
  })
  const pages = doc.internal.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    const bottom = doc.internal.pageSize.getHeight()
    doc.setDrawColor(...border)
    doc.line(margin, bottom - 35, right, bottom - 35)
    text(`Generado: ${generatedAt}`, margin, bottom - 21, 7.5, false, muted)
    text(`Página ${page} de ${pages}`, right, bottom - 21, 7.5, false, muted, { align: 'right' })
  }
  return doc
}

export const buildRouteExecutionPdf = (options) => buildUserReportPdf(options)
